import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  Truck,
  Boxes,
  Warehouse,
  ArrowLeftRight,
  Receipt,
  Users,
  Building2,
  DollarSign,
  UserCheck,
  Clock,
  AlertOctagon,
  RotateCcw,
  BarChart3,
  Bell,
  ShieldCheck,
  History,
  Settings,
  ChevronLeft,
  Store,
  Sparkles
} from "lucide-react";
import { useAuth } from "../../app/context/AuthContext";
import { useI18n } from "../../i18n/i18n";
import { useApp } from "../../app/context/AppContext";

export function Sidebar({ collapsed, onToggle }) {
  const { hasPermission, role } = useAuth();
  const { t } = useI18n();
  const { metrics } = useApp();

  const navigationItems = [
    {
      group: "Core Operations",
      items: [
        { name: t("dashboard"), path: "/", icon: LayoutDashboard, key: "dashboard" },
        {
          name: t("sales"),
          path: "/sales",
          icon: ShoppingCart,
          key: "sales",
          badge: "POS"
        },
        { name: t("inventory"), path: "/inventory", icon: Boxes, key: "inventory" },
        { name: t("warehouse"), path: "/warehouse", icon: Warehouse, key: "warehouse" },
        { name: t("stockTransfer"), path: "/stock-transfer", icon: ArrowLeftRight, key: "stockTransfer" }
      ]
    },
    {
      group: "Procurement & Inward",
      items: [
        { name: t("procurement"), path: "/procurement", icon: Truck, key: "procurement" },
        { name: t("receiving"), path: "/receiving", icon: Store, key: "receiving" },
        { name: t("suppliers"), path: "/suppliers", icon: Building2, key: "suppliers" }
      ]
    },
    {
      group: "Stock Intelligence",
      items: [
        {
          name: t("batchExpiry"),
          path: "/batch-expiry",
          icon: Clock,
          key: "batchExpiry",
          count: metrics.expiringSoonCount,
          countColor: "bg-amber-500 text-white"
        },
        {
          name: t("deadStock"),
          path: "/dead-stock",
          icon: AlertOctagon,
          key: "deadStock",
          count: metrics.lowStockCount,
          countColor: "bg-rose-500 text-white"
        },
        { name: t("returns"), path: "/returns", icon: RotateCcw, key: "returns" }
      ]
    },
    {
      group: "Commercial & Business",
      items: [
        { name: t("customers"), path: "/customers", icon: Users, key: "customers" },
        { name: t("finance"), path: "/finance", icon: DollarSign, key: "finance" },
        { name: t("employees"), path: "/employees", icon: UserCheck, key: "employees" },
        { name: t("analytics"), path: "/analytics", icon: BarChart3, key: "analytics" }
      ]
    },
    {
      group: "Governance & System",
      items: [
        {
          name: t("notifications"),
          path: "/notifications",
          icon: Bell,
          key: "notifications",
          count: metrics.lowStockCount + metrics.expiringSoonCount
        },
        { name: t("auditLog"), path: "/audit-log", icon: History, key: "auditLog" },
        { name: t("accessControl"), path: "/access-control", icon: ShieldCheck, key: "accessControl" },
        { name: t("settings"), path: "/settings", icon: Settings, key: "settings" }
      ]
    }
  ];

  return (
    <aside
      className={`fixed top-0 bottom-0 left-0 z-40 flex flex-col border-r border-slate-200/80 dark:border-slate-800 bg-slate-900 text-slate-100 transition-all duration-300 ${
        collapsed ? "w-20" : "w-64"
      }`}
    >
      {/* Brand Header */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-slate-800 bg-slate-950/80">
        {!collapsed && (
          <div className="flex items-center gap-2.5 truncate">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-400 text-white shadow-lg shadow-emerald-600/30">
              <Store className="h-5 w-5" />
            </div>
            <div className="truncate">
              <h1 className="text-sm font-bold tracking-tight text-white font-display">
                Kirana Inventory
              </h1>
              <p className="text-[10px] text-emerald-400 font-mono tracking-wider">
                Enterprise SaaS
              </p>
            </div>
          </div>
        )}

        {collapsed && (
          <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
            <Store className="h-5 w-5" />
          </div>
        )}

        <button
          onClick={onToggle}
          className="hidden lg:flex p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Role Banner */}
      {!collapsed && (
        <div className="px-4 py-2.5 bg-slate-800/40 border-b border-slate-800 text-[11px] flex items-center justify-between text-slate-300">
          <span className="text-slate-400">Active Role:</span>
          <span className="font-semibold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded-md border border-emerald-800/50">
            {role}
          </span>
        </div>
      )}

      {/* Navigation Groups */}
      <div className="flex-1 overflow-y-auto p-3 space-y-6">
        {navigationItems.map((group, gIdx) => {
          // Filter items by role permissions
          const visibleItems = group.items.filter((item) => hasPermission(item.key));
          if (visibleItems.length === 0) return null;

          return (
            <div key={gIdx} className="space-y-1">
              {!collapsed && (
                <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {group.group}
                </p>
              )}
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      title={collapsed ? item.name : undefined}
                      className={({ isActive }) =>
                        `flex items-center justify-between rounded-xl px-3 py-2 text-xs font-medium transition-all ${
                          isActive
                            ? "bg-emerald-600 text-white font-semibold shadow-md shadow-emerald-600/20"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        } ${collapsed ? "justify-center px-0" : ""}`
                      }
                    >
                      <div className="flex items-center gap-3 truncate">
                        <Icon className="w-4 h-4 shrink-0" />
                        {!collapsed && <span className="truncate">{item.name}</span>}
                      </div>

                      {!collapsed && (
                        <div className="flex items-center gap-1.5">
                          {item.badge && (
                            <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300">
                              {item.badge}
                            </span>
                          )}
                          {item.count > 0 && (
                            <span
                              className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                                item.countColor || "bg-slate-700 text-slate-200"
                              }`}
                            >
                              {item.count}
                            </span>
                          )}
                        </div>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Tagline */}
      {!collapsed && (
        <div className="p-3.5 border-t border-slate-800 bg-slate-950/60 text-[10px] text-slate-400 text-center">
          <p className="font-mono text-emerald-400 font-medium">Kirana Multi-WH v1.0</p>
          <p className="truncate text-slate-500 mt-0.5">One system. Every product. Real-time.</p>
        </div>
      )}
    </aside>
  );
}
